import type { AdminAiFeaturesTreeNodeProps } from "../../models/admin-ai-features.models";
import { adminAiScansStyles } from "../../styles/admin-ai-scans.styles";
import { getAiFeaturesCountItems } from "../../utils/admin-ai-features.utils";
import AdminAiScanTreeSummary from "./AdminAiScanTreeSummary";
import { useState } from "react";

// Render the inventory in its original level order, keeping stable IDs beside the human labels.
// Native details keep manual expansion local to each branch; page commands remount the tree with a new default.
const AdminAiFeaturesTreeNode = ({ node, expansion }: AdminAiFeaturesTreeNodeProps) => {
  const [open, setOpen] = useState(
    expansion === "behaviors" || (expansion === "tree" && node.level !== "featureGroup"),
  );

  const counts = getAiFeaturesCountItems(node.counts);

  return (
    <details
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      data-test={`admin-ai-features-${node.level}-${node.id}`}
      className={adminAiScansStyles.treeBranch}
    >
      <AdminAiScanTreeSummary label={node.label} id={node.id} kind={node.kind} counts={counts} />
      {open && (
        <div className={adminAiScansStyles.treeChildren}>
          {node.children.length > 0 ? (
            node.level === "featureGroup" ? (
              <div className="overflow-x-auto pb-3" data-test="admin-ai-features-behavior-table">
                <table className="w-full min-w-[480px] table-fixed border-collapse text-left text-sm">
                  <caption className="sr-only">Behaviors for {node.label}</caption>
                  <thead className="bg-slate-100 text-[10px] uppercase tracking-wide text-slate-500 dark:bg-gray-700 dark:text-gray-300">
                    <tr>
                      <th scope="col" className="px-3 py-2 font-bold">
                        Behavior
                      </th>
                      <th scope="col" className="w-1/4 px-3 py-2 font-bold">
                        Stable ID
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-slate-50 text-slate-700 dark:divide-gray-600 dark:bg-gray-900/40 dark:text-gray-200">
                    {node.children.map((child) => (
                      <tr key={child.id} data-test={`admin-ai-features-behavior-${child.id}`}>
                        <td className="px-3 py-2.5 align-top leading-6">{child.label}</td>
                        <td className="break-words px-3 py-2.5 align-top font-mono text-[10px] leading-5 text-slate-500 dark:text-gray-400">
                          {child.id}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              node.children.map((child) => (
                <AdminAiFeaturesTreeNode key={child.id} node={child} expansion={expansion} />
              ))
            )
          ) : (
            <p className="py-3 text-xs opacity-60">
              No{" "}
              {node.level === "businessObject" ? "surfaces" : node.level === "surface" ? "feature groups" : "behaviors"}{" "}
              recorded.
            </p>
          )}
        </div>
      )}
    </details>
  );
};

export default AdminAiFeaturesTreeNode;
